<script setup>
import {ref} from 'vue';
import {Inertia} from '@inertiajs/inertia';
import AppLayout from '@/Layouts/AppLayout.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import PaginationLinks from '@/Components/PaginationLinks.vue';
import DialogModal from '@/Components/DialogModal.vue';
import KnowledgebaseEdit from './KnowledgebaseEdit.vue';

const props = defineProps({
  articles: Object,
  pageSearchQuery: String,
});

const searchQuery = ref(props.pageSearchQuery);
const searching = ref(false);
const deletingArticle = ref(null);

const openDeleteArticleModal = (article) => {
  deletingArticle.value = article;
};
const closeDeleteArticleModal = () => {
  deletingArticle.value = null;
};

const makeSearchRequest = _.debounce(() => {
  searching.value = true;
  Inertia.get(route('admin.knowledgebase.index'), {
    query: searchQuery.value,
  }, {preserveScroll: true});
  searching.value = false;
}, 400);

const deleteArticle = (articleID) => {
  Inertia.delete(route('admin.knowledgebase.destroy', { knowledgebase: articleID}), {
    preserveScroll: true,
    onError: (error) => alert(`There was an error. Try again later`),
  });
  deletingArticle.value = null;
};

</script>

<template>
  <AppLayout title="Knowledge Base">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">Knowledge Base Articles</h2>
    </template>

    <div class="mx-auto py-10 sm:px-6 lg:px-8">
      <div class="mb-4 flex justify-between items-center">
        <TextInput v-model="searchQuery" placeholder="Search articles by title..." @input="makeSearchRequest" class="w-1/2"/>
        <a :href="route('admin.knowledgebase.create')">
          <PrimaryButton>Create New Article</PrimaryButton>
        </a>
      </div>

      <table class="min-w-full w-full text-left text-sm font-light">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-2">Title</th>
            <th class="px-4 py-2">Subject</th>
            <th class="px-4 py-2">Keyword</th>
            <th class="px-4 py-2">Author</th>
            <th class="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="article in articles.data" :key="article.articleID" class="border-b">
            <td class="px-4 py-2">{{ article.title }}</td>
            <td class="px-4 py-2">{{ article.subject }}</td>
            <td class="px-4 py-2">{{ article.keyword }}</td>
            <td class="px-4 py-2">{{ article.author }}</td>
            <td class="px-4 py-2">
              <a :href="route('admin.knowledgebase.show', article.articleID)" class="fa fa-eye mr-2" title="View" />
              <a :href="route('admin.knowledgebase.edit', article.articleID)" class="fa fa-pen mr-2" title="Edit" />
              <button @click="openDeleteArticleModal(article)" class="fa fa-trash" title="Delete" />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="mt-4">
        <PaginationLinks :pagination="articles"/>
      </div>
    </div>

    <DialogModal :show="deletingArticle !== null" @close="closeDeleteArticleModal">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        You are about to delete article <b v-text="deletingArticle?.title"></b>.
        <br>Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeDeleteArticleModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="deleteArticle(deletingArticle.articleID)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>
